import { Tooltip } from '@shared/components/tooltip';
import { useLogosContext } from '@shared/contexts/pluginContexts/LogosURLsContext';
import { RatingMethod } from '@shared/generated/graphql';

export const RateLogo = ({
  rateMethod,
  integrationImageOverride,
  integrationNameOverride,
}: {
  rateMethod?: RatingMethod;
  integrationImageOverride?: string;
  integrationNameOverride?: string;
}) => {
  const logos = useLogosContext();
  if (!rateMethod) return null;

  const gsDefaultName = 'Triumph';
  const datDefaultName = 'DAT';
  const dtsDefaultName = 'Direct Traffic Solutions';
  const sunsetDefaultName = 'Sunset';
  const tabiDefaultName = 'Tabi';
  const wernerDefaultName = 'Werner';
  const arcbestDefaultName = 'ArcBest';
  const echoDefaultName = 'Echo';
  const transfixDefaultName = 'Transfix';
  const bitfreighterDefaultName = 'Bitfreighter';
  const WWEXDefaultName = 'WWEX';
  const FastmoreDefaultName = 'Fastmore';
  const WorldwideLogisticsDefaultName = 'Worldwide Logistics';
  const AVRL = 'AVRL';
  const TRUCKSTOP = 'Truckstop';
  const Sonar = 'Sonar';
  const NTG = 'Nolan Transportation Group';
  const SOFTMODAL = 'Softmodal';
  const drayratesDefaultName = 'Drayrates';
  const tooltipContent = (name: string) =>
    `Rate sourced from ${integrationNameOverride ?? name} `;

  switch (rateMethod) {
    case RatingMethod.DAT:
      return (
        <Logo
          src={integrationImageOverride ?? logos.DAT}
          tooltip={tooltipContent(datDefaultName)}
          width={48}
        />
      );
    case RatingMethod.DRAYRATES_RATING:
      return (
        <Logo
          imgClassName="rounded-[4px]"
          src={integrationImageOverride ?? logos.DRAYRATES}
          tooltip={tooltipContent(drayratesDefaultName)}
        />
      );
    case RatingMethod.DTS_RATING:
      return (
        <Logo
          src={integrationImageOverride ?? logos.DTS}
          tooltip={tooltipContent(dtsDefaultName)}
        />
      );
    case RatingMethod.GREENSCREENS:
      return (
        <Logo
          src={integrationImageOverride ?? logos.TRIUMPH}
          tooltip={tooltipContent(gsDefaultName)}
        />
      );
    case RatingMethod.SUNSET:
      return (
        <Logo
          src={integrationImageOverride ?? logos.SUNSET}
          tooltip={tooltipContent(sunsetDefaultName)}
        />
      );
    case RatingMethod.TABI:
      return (
        <Logo
          src={integrationImageOverride ?? logos.TABI}
          tooltip={tooltipContent(tabiDefaultName)}
        />
      );
    case RatingMethod.WERNER:
      return (
        <Logo
          src={integrationImageOverride ?? logos.WERNER}
          tooltip={tooltipContent(wernerDefaultName)}
        />
      );
    case RatingMethod.ARCBEST_TL_QUOTING:
      return (
        <Logo
          src={integrationImageOverride ?? logos.ARCBEST}
          tooltip={tooltipContent(arcbestDefaultName)}
        />
      );
    case RatingMethod.ECHO:
      return (
        <Logo
          src={integrationImageOverride ?? logos.ECHO}
          tooltip={tooltipContent(echoDefaultName)}
        />
      );
    case RatingMethod.TRANSFIX:
      return (
        <Logo
          imgClassName="rounded-[4px]"
          src={integrationImageOverride ?? logos.TRANSFIX}
          tooltip={tooltipContent(transfixDefaultName)}
        />
      );
    case RatingMethod.BITFREIGHTER:
      return (
        <Logo
          imgClassName="rounded-[4px]"
          src={integrationImageOverride ?? logos.BITFREIGHTER}
          tooltip={tooltipContent(bitfreighterDefaultName)}
        />
      );
    case RatingMethod.WWEX:
      return (
        <Logo
          imgClassName="rounded-[4px]"
          src={integrationImageOverride ?? logos.WWEX}
          tooltip={tooltipContent(WWEXDefaultName)}
        />
      );
    case RatingMethod.FASTMORE:
      return (
        <Logo
          imgClassName="rounded-[4px]"
          src={integrationImageOverride ?? logos.FASTMORE}
          tooltip={tooltipContent(FastmoreDefaultName)}
        />
      );
    case RatingMethod.WORLDWIDE_LOGISTICS:
      return (
        <Logo
          imgClassName="rounded-[4px]"
          src={integrationImageOverride ?? logos.WORLDWIDE_LOGISTICS}
          tooltip={tooltipContent(WorldwideLogisticsDefaultName)}
        />
      );
    case RatingMethod.AVRL:
      return (
        <Logo
          imgClassName="rounded-[4px]"
          src={integrationImageOverride ?? logos.AVRL}
          tooltip={tooltipContent(AVRL)}
        />
      );
    case RatingMethod.TRUCKSTOP:
      return (
        <Logo
          width={20}
          src={integrationImageOverride ?? logos.TRUCKSTOP}
          tooltip={tooltipContent(TRUCKSTOP)}
        />
      );
    case RatingMethod.SONAR:
      return (
        <Logo
          src={integrationImageOverride ?? logos.SONAR}
          tooltip={tooltipContent(Sonar)}
        />
      );
    case RatingMethod.NTG:
      return (
        <Logo
          imgClassName="rounded-[4px]"
          src={integrationImageOverride ?? logos.NTG}
          tooltip={tooltipContent(NTG)}
        />
      );
    case RatingMethod.SOFTMODAL:
      return (
        <Logo
          imgClassName="rounded-[4px]"
          src={integrationImageOverride ?? logos.SOFTMODAL}
          tooltip={tooltipContent(SOFTMODAL)}
        />
      );
    case RatingMethod.CUSTOM:
      return (
        <Logo
          imgClassName="rounded-[4px] py-[3px] px-[1px]"
          src={logos.CUSTOM}
          tooltip={tooltipContent(
            integrationNameOverride ?? 'internal pricing strategy'
          )}
        />
      );
    default:
      return null;
  }
};

type LogoProps = {
  src: string;
  tooltip: string;
  width?: number;
  height?: number;
  imgClassName?: string;
};
function Logo({ src, tooltip, imgClassName, width = 32, height }: LogoProps) {
  return (
    <Tooltip content={tooltip} isInstant>
      <div className="rounded-md border p-[2px]">
        <img
          className={imgClassName}
          width={width}
          height={height}
          src={src}
          alt={tooltip}
        />
      </div>
    </Tooltip>
  );
}
